/**
 * nodeDelete.ts - Centralized delete logic for automation flow nodes
 *
 * Provides pure functions for:
 * - Protecting trigger nodes, default segments, and behavior-case nodes from direct deletion
 * - Cascading switch deletion to branch nodes, hidden switch node, and phantom target
 * - Reconnecting edges when a mid-chain node is deleted (A→B→C becomes A→C)
 * - Re-laying out the graph after structural changes
 *
 * Related:
 *   - utils/nodeReordering.ts - Reuses getChildNodeIds, getDescendantNodeIds
 *   - utils/layoutEngine.ts - Re-applies layout after deletion
 *   - hooks/useCanvasKeyboardShortcuts.ts - Keyboard delete handler
 *   - hooks/useContextMenuActions.ts - Context menu delete handler
 */

import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData, SegmentNodeData } from '../types';
import type { AutomationStepNodeData, BehaviorCaseNodeData } from '../types/stepNodes';
import { NODE_TYPES } from '../types';
import { getChildNodeIds, getDescendantNodeIds } from './nodeReordering';
import { applyLayout } from './layoutEngine';
import { applyLocalSegmentOrder, getOrderedSegments } from './segmentReordering';

/**
 * Metadata about a deleted step, extracted for API persistence.
 */
export interface DeletedStepInfo {
	/** The React Flow node ID that was deleted */
	nodeId?: string;
	/** The server-side step ID */
	stepId: string;
	/** The automation step type enum for the API call */
	stepType: Api.AutomationStepType;
	/**
	 * The template ID that owns this step. For root-level steps this is the main
	 * template ID; for branch steps inside a switch case this is the child
	 * template ID stored on the case.
	 */
	ownerTemplateId?: string;
}

/**
 * Result of a deleteNodes operation.
 */
export interface DeleteNodesResult {
	/** Updated node list with deleted nodes removed */
	nodes: Node<BaseNodeData>[];
	/** Updated edge list with orphaned edges removed and reconnections applied */
	edges: Edge[];
	/** Number of nodes actually deleted */
	deletedCount: number;
	/** IDs of nodes that were requested for deletion but protected */
	protectedNodeIds: string[];
	/** Metadata for each deleted step node (used to persist deletions to the API) */
	deletedSteps: DeletedStepInfo[];
}

export interface BranchCaseDeletionPlan {
	/** Segment branch cases that should be persisted through the case-delete endpoint */
	casesToDelete: { switchNodeId: string; caseId: string }[];
	/** Nodes whose step deletions should NOT be persisted individually */
	skipStepDeletionNodeIds: Set<string>;
}

type BranchCaseNodeData = SegmentNodeData | BehaviorCaseNodeData;

function isBranchCaseNode(node: Node<BaseNodeData>): boolean {
	return node.type === NODE_TYPES.SEGMENT || node.type === NODE_TYPES.BEHAVIOR_CASE;
}

function getBranchCaseData(node: Node<BaseNodeData>): BranchCaseNodeData | null {
	if (!isBranchCaseNode(node)) {
		return null;
	}

	return node.data as BranchCaseNodeData;
}

/**
 * Check whether a node is protected from direct deletion.
 *
 * Protected nodes:
 * - Trigger nodes (the automation entry point)
 * - Default branch-case nodes (both segments and behavior cases)
 *
 * Non-default behavior cases and segments can be deleted directly.
 * Protected nodes CAN still be removed during cascade deletion (e.g., when
 * the entire switch structure is removed). The `isCascade` flag controls this.
 */
export function isProtectedNode(node: Node<BaseNodeData>, isCascade = false): boolean {
	// Trigger nodes are always protected, even during cascade.
	if (node.type === NODE_TYPES.TRIGGER) {
		return true;
	}

	if (isCascade) {
		return false;
	}

	// Default branch-case nodes (segments and behavior cases) are protected from direct
	// deletion but removed during cascade. Non-default cases can be deleted directly.
	if (isBranchCaseNode(node)) {
		const branchCaseData = getBranchCaseData(node);
		if (branchCaseData?.isDefault) {
			return true;
		}
	}

	return false;
}

/**
 * Collect all nodes that must be cascade-deleted when a switch's visual parent
 * (the step node that feeds into branch cases) is deleted.
 *
 * A "visual parent" is the step whose outgoing edges target segment or
 * behavior-case nodes.
 * When this node is deleted, the entire switch structure becomes orphaned:
 * - All branch entry nodes for this switch
 * - The hidden switch node
 * - The phantom target node
 * - All descendants under each branch
 *
 * This function looks at the requested deletion set and finds any node whose
 * outgoing edges target branch case nodes. For each such node, it collects the
 * full switch subtree.
 */
export function collectSwitchCascadeIds(
	requestedIds: Set<string>,
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): Set<string> {
	const cascadeIds = new Set<string>();
	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	for (const nodeId of requestedIds) {
		const node = nodeMap.get(nodeId);
		if (!node) {
			continue;
		}

		// Check if this node's children include branch case nodes.
		const childIds = getChildNodeIds(nodeId, edges);
		const childBranchCases = childIds
			.map(id => nodeMap.get(id))
			.filter((n): n is Node<BaseNodeData> => n !== undefined && isBranchCaseNode(n));

		if (childBranchCases.length === 0) {
			continue;
		}

		// This node is a visual parent of a switch. Collect the entire switch subtree.
		for (const branchCaseNode of childBranchCases) {
			const branchCaseData = getBranchCaseData(branchCaseNode);
			if (!branchCaseData) {
				continue;
			}
			const switchNodeId = branchCaseData.parentSwitchNodeId;

			// Find ALL branch cases belonging to this switch (not just ones connected to this node).
			const allSwitchBranchCases = nodes.filter(candidate => {
				const candidateData = getBranchCaseData(candidate);
				return candidateData?.parentSwitchNodeId === switchNodeId;
			});

			for (const branchCase of allSwitchBranchCases) {
				cascadeIds.add(branchCase.id);
				// Collect all descendants under each branch entry node.
				const descendants = getDescendantNodeIds(branchCase.id, edges);
				for (const descendantId of descendants) {
					cascadeIds.add(descendantId);
				}
			}

			// Add the hidden switch node itself.
			if (nodeMap.has(switchNodeId)) {
				cascadeIds.add(switchNodeId);
			}

			// Add the phantom target node.
			const phantomTargetId = `phantom-target-${switchNodeId}`;
			if (nodeMap.has(phantomTargetId)) {
				cascadeIds.add(phantomTargetId);
			}
		}
	}

	return cascadeIds;
}

/**
 * Collect all nodes that must be cascade-deleted when a branch case node is deleted.
 *
 * When deleting a non-default branch case, we want to remove:
 * - The branch case node itself
 * - All descendant nodes under that branch (steps inside the branch)
 *
 * Additionally, if deleting the branch would leave a switch with only the default
 * case remaining, we remove the entire switch structure:
 * - Default branch case + all of its descendants
 * - The hidden switch node
 * - The phantom target node
 */
export function collectBranchCascadeIds(
	requestedIds: Set<string>,
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): Set<string> {
	const cascadeIds = new Set<string>();
	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	// Group requested direct branch deletions by parent switch.
	const branchCasesToDeleteBySwitch = new Map<string, string[]>();

	for (const nodeId of requestedIds) {
		const node = nodeMap.get(nodeId);
		const branchCaseData = node ? getBranchCaseData(node) : null;
		if (!node || !branchCaseData) {
			continue;
		}

		// Default branch cases are protected from direct deletion; ignore if present.
		if (branchCaseData.isDefault) {
			continue;
		}

		const list = branchCasesToDeleteBySwitch.get(branchCaseData.parentSwitchNodeId) ?? [];
		list.push(nodeId);
		branchCasesToDeleteBySwitch.set(branchCaseData.parentSwitchNodeId, list);

		// Always delete the branch case's descendants.
		const descendants = getDescendantNodeIds(nodeId, edges);
		for (const descendantId of descendants) {
			cascadeIds.add(descendantId);
		}
	}

	// If we're deleting all non-default branch cases for a switch, remove the whole switch.
	for (const [switchNodeId, branchCaseIdsToDelete] of branchCasesToDeleteBySwitch.entries()) {
		const allBranchCases = nodes.filter(candidate => {
			const branchCaseData = getBranchCaseData(candidate);
			return branchCaseData?.parentSwitchNodeId === switchNodeId;
		});

		const nonDefaultBranchCases = allBranchCases.filter(candidate => {
			const branchCaseData = getBranchCaseData(candidate);
			return branchCaseData?.isDefault !== true;
		});
		const deletingAllNonDefault =
			nonDefaultBranchCases.length > 0 &&
			nonDefaultBranchCases.every(candidate => branchCaseIdsToDelete.includes(candidate.id));

		if (!deletingAllNonDefault) {
			continue;
		}

		// Add ALL branch cases (including default) + descendants.
		for (const branchCase of allBranchCases) {
			cascadeIds.add(branchCase.id);
			const descendants = getDescendantNodeIds(branchCase.id, edges);
			for (const descendantId of descendants) {
				cascadeIds.add(descendantId);
			}
		}

		// Add hidden switch node.
		if (nodeMap.has(switchNodeId)) {
			cascadeIds.add(switchNodeId);
		}

		// Add phantom target node.
		const phantomTargetId = `phantom-target-${switchNodeId}`;
		if (nodeMap.has(phantomTargetId)) {
			cascadeIds.add(phantomTargetId);
		}
	}

	return cascadeIds;
}

/**
 * Builds the persistence plan for deleting branch cases.
 *
 * Both segment and behavior-case nodes participate in this plan. Each branch
 * case deletion is persisted through the case-delete endpoint using the
 * switchNodeId and caseId pair.
 *
 * Branch descendants should not also be sent to the step-delete endpoint.
 * When deleting the last non-default branch of a switch, the UI collapses the
 * entire switch, which means the hidden switch node and the default branch
 * subtree must also be excluded from per-step persistence.
 */
export function buildBranchCaseDeletionPlan(
	requestedNodeIds: string[],
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): BranchCaseDeletionPlan {
	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	const caseKeySet = new Set<string>();
	const casesToDelete: { switchNodeId: string; caseId: string }[] = [];
	const skipStepDeletionNodeIds = new Set<string>();
	const branchCasesBySwitch = new Map<string, string[]>();

	for (const nodeId of requestedNodeIds) {
		const node = nodeMap.get(nodeId);
		const branchCaseData = node ? getBranchCaseData(node) : null;
		if (!node || !branchCaseData || branchCaseData.isDefault) {
			continue;
		}

		const caseKey = `${branchCaseData.parentSwitchNodeId}:${branchCaseData.caseId}`;
		if (!caseKeySet.has(caseKey)) {
			caseKeySet.add(caseKey);
			casesToDelete.push({
				switchNodeId: branchCaseData.parentSwitchNodeId,
				caseId: branchCaseData.caseId,
			});
		}

		skipStepDeletionNodeIds.add(node.id);
		for (const descendantId of getDescendantNodeIds(node.id, edges)) {
			skipStepDeletionNodeIds.add(descendantId);
		}

		const groupedNodeIds = branchCasesBySwitch.get(branchCaseData.parentSwitchNodeId) ?? [];
		groupedNodeIds.push(node.id);
		branchCasesBySwitch.set(branchCaseData.parentSwitchNodeId, groupedNodeIds);
	}

	for (const [switchNodeId, selectedBranchCaseIds] of branchCasesBySwitch.entries()) {
		const allBranchCases = nodes.filter(candidate => {
			const branchCaseData = getBranchCaseData(candidate);
			return branchCaseData?.parentSwitchNodeId === switchNodeId;
		});
		const nonDefaultBranchCases = allBranchCases.filter(candidate => {
			const branchCaseData = getBranchCaseData(candidate);
			return branchCaseData?.isDefault !== true;
		});

		const deletingAllNonDefault =
			nonDefaultBranchCases.length > 0 &&
			nonDefaultBranchCases.every(candidate => selectedBranchCaseIds.includes(candidate.id));
		if (!deletingAllNonDefault) {
			continue;
		}

		skipStepDeletionNodeIds.add(switchNodeId);

		const defaultBranchCase = allBranchCases.find(candidate => getBranchCaseData(candidate)?.isDefault === true);
		if (!defaultBranchCase) {
			continue;
		}

		skipStepDeletionNodeIds.add(defaultBranchCase.id);
		for (const descendantId of getDescendantNodeIds(defaultBranchCase.id, edges)) {
			skipStepDeletionNodeIds.add(descendantId);
		}
	}

	return {
		casesToDelete,
		skipStepDeletionNodeIds,
	};
}

/**
 * Build reconnection edges for mid-chain deletions.
 *
 * When node B is deleted from chain A→B→C, we need to create edge A→C.
 * This only applies when B has exactly ONE incoming edge and ONE outgoing edge
 * (simple chain case). Multi-parent or multi-child nodes do not reconnect.
 *
 * The reconnection edge preserves the sourceHandle from the incoming edge
 * (A→B's sourceHandle), since that determines which output port of A is used.
 */
export function buildReconnectionEdges(deletionIds: Set<string>, edges: Edge[]): Edge[] {
	const reconnections: Edge[] = [];

	for (const deletedId of deletionIds) {
		// Find all incoming edges to this node.
		const incomingEdges = edges.filter(e => e.target === deletedId && !deletionIds.has(e.source));

		// Find all outgoing edges from this node.
		const outgoingEdges = edges.filter(e => e.source === deletedId && !deletionIds.has(e.target));

		// Only reconnect in the simple single-in, single-out case.
		if (incomingEdges.length !== 1 || outgoingEdges.length !== 1) {
			continue;
		}

		const incoming = incomingEdges[0];
		const outgoing = outgoingEdges[0];

		reconnections.push({
			id: `edge-${incoming.source}-${outgoing.target}`,
			source: incoming.source,
			target: outgoing.target,
			sourceHandle: incoming.sourceHandle,
			type: 'default',
		});
	}

	return reconnections;
}

/**
 * Delete nodes from the automation flow with protection, cascade, and reconnection.
 *
 * This is the single entry point for all node deletion operations (keyboard and
 * context menu). It:
 * 1. Filters out protected nodes (triggers, default branch nodes) from direct deletion
 * 2. Cascades switch deletion to all related nodes (branch cases, hidden switch, phantom target)
 * 3. Reconnects edges when a mid-chain node is removed
 * 4. Re-layouts the graph via applyLayout
 *
 * @param requestedNodeIds - IDs of nodes the user wants to delete
 * @param nodes - Current node list
 * @param edges - Current edge list
 * @returns DeleteNodesResult with updated nodes, edges, and metadata
 */
export function deleteNodes(requestedNodeIds: string[], nodes: Node<BaseNodeData>[], edges: Edge[]): DeleteNodesResult {
	if (requestedNodeIds.length === 0) {
		return { nodes, edges, deletedCount: 0, protectedNodeIds: [], deletedSteps: [] };
	}

	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	// Step 1: Partition requested IDs into deletable and protected.
	const directDeleteIds = new Set<string>();
	const protectedNodeIds: string[] = [];

	for (const nodeId of requestedNodeIds) {
		const node = nodeMap.get(nodeId);
		if (!node) {
			continue;
		}
		if (isProtectedNode(node, false)) {
			protectedNodeIds.push(nodeId);
		} else {
			directDeleteIds.add(nodeId);
		}
	}

	if (directDeleteIds.size === 0) {
		return { nodes, edges, deletedCount: 0, protectedNodeIds, deletedSteps: [] };
	}

	// Step 2: Collect cascade IDs (switch subtrees).
	const cascadeIds = collectSwitchCascadeIds(directDeleteIds, nodes, edges);

	// Step 2b: Collect cascade IDs for branch-case deletions (delete branch descendants,
	// and collapse the entire switch when only the default case would remain).
	const segmentCascadeIds = collectBranchCascadeIds(directDeleteIds, nodes, edges);

	// Merge direct + cascades into the final deletion set.
	const allDeletionIds = new Set<string>(directDeleteIds);
	for (const id of cascadeIds) {
		allDeletionIds.add(id);
	}
	for (const id of segmentCascadeIds) {
		allDeletionIds.add(id);
	}

	// Step 3: Build reconnection edges before removing anything.
	const reconnections = buildReconnectionEdges(allDeletionIds, edges);

	// Step 4: Remove nodes and orphaned edges.
	const remainingNodes = nodes.filter(n => !allDeletionIds.has(n.id));
	const remainingEdges = edges.filter(e => !allDeletionIds.has(e.source) && !allDeletionIds.has(e.target));

	// Step 5: Add reconnection edges.
	const finalEdges = [...remainingEdges, ...reconnections];

	// Step 6: Normalize any remaining segment switches whose branches changed.
	// Without this, right-click deletion removes the segment node itself but leaves
	// sibling case metadata stale until the server refetch completes.
	const affectedSegmentSwitchNodeIds = new Set<string>();
	for (const id of allDeletionIds) {
		const node = nodeMap.get(id);
		if (node?.type !== NODE_TYPES.SEGMENT) {
			continue;
		}

		const segmentData = node.data as SegmentNodeData;
		affectedSegmentSwitchNodeIds.add(segmentData.parentSwitchNodeId);
	}

	let normalizedNodes = remainingNodes;
	let normalizedEdges = finalEdges;
	for (const switchNodeId of affectedSegmentSwitchNodeIds) {
		if (!normalizedNodes.some(node => node.id === switchNodeId)) {
			continue;
		}

		const orderedRemainingSegmentIds = getOrderedSegments(switchNodeId, normalizedNodes, normalizedEdges).map(
			segmentNode => segmentNode.id
		);
		if (orderedRemainingSegmentIds.length === 0) {
			continue;
		}

		const normalizedGraph = applyLocalSegmentOrder(
			switchNodeId,
			normalizedNodes,
			normalizedEdges,
			orderedRemainingSegmentIds
		);
		normalizedNodes = normalizedGraph.nodes;
		normalizedEdges = normalizedGraph.edges;
	}

	// Step 7: Collect deleted step metadata for API persistence.
	const deletedSteps: DeletedStepInfo[] = [];
	for (const id of allDeletionIds) {
		const node = nodeMap.get(id);
		if (!node) {
			continue;
		}
		const data = node.data as AutomationStepNodeData;
		// Only step nodes have stepId/stepType — skip triggers, segments, phantom nodes, etc.
		if (data.stepId && data.stepType) {
			const info: DeletedStepInfo = {
				nodeId: id,
				stepId: data.stepId,
				stepType: data.stepType,
			};
			// Resolve the owning template: automationTemplate.id gives us the template
			// that contains this step. For branch steps this is the child template.
			if (data.automationTemplate?.id) {
				info.ownerTemplateId = data.automationTemplate.id;
			}
			deletedSteps.push(info);
		}
	}

	// Step 8: Re-layout.
	const layoutResult = applyLayout(normalizedNodes, normalizedEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		deletedCount: allDeletionIds.size,
		protectedNodeIds,
		deletedSteps,
	};
}
